/* eslint-disable max-len */
/** @jsxImportSource @emotion/react */
import { FunctionComponent, useState, useEffect } from 'react';
import { css } from '@emotion/react';
import Modal from 'react-bootstrap/Modal';
import JsonQuery from 'json-query';
import _ from 'lodash';
import { hooks as commonHooks, types as commonTypes } from '@sitebuilder/common';
import HeaderWrapper from '../Header/HeaderWrapper';
import { Headings } from '../Header/Header';
import {
    getTextSchemaProperties,
    getStyleSchemaProperties,
    getLayoutSchemaProperties,
    isLocalStorageAccessible,
} from '../utils';
import modalStyles, { themeStyles } from './ModalOnLoad.styles';

const CHILDREN_PLACEHOLDER_ID = 'modal';
interface WidgetProperties extends commonTypes.WidgetPropertiesBase {
    id?: string;
    titles?: Headings[];
    closeButtonHtml?: string;
    annotationTag?: string;
    acceptButtonText?: string;
    acceptButtonAriaLabel?: string;
    declineButtonText?: string;
    declineButtonAriaLabel?: string;
    showModalInEditMode?: boolean;
    typographyClass?: string;
    linkOnDecline?: string;
    rememberCheckboxLabel?: string;
    linkTarget?: string;
    storageName?: string;
    itemToAddInStorage?: string;
}

const ModalOnLoadContainer: FunctionComponent<
    React.PropsWithChildren<commonTypes.WidgetInstanceProps>
> = (props: commonTypes.WidgetInstanceProps) => {
    const i18n = commonHooks.useLocalization();
    const widgetInstance = commonHooks.useWidgetInstance(props.widgetInstanceId);
    const widgetProperties =
        commonHooks.useWidgetProperties<WidgetProperties>(props.widgetInstanceId) ||
        props?.properties;
    const {
        styles,
        id,
        titles,
        annotationTag,
        acceptButtonText,
        declineButtonText,
        showModalInEditMode,
        ...rest
    } = widgetProperties || props;
    const { data } = props;
    const theme = data?.themeSelected || props?.theme;
    const { inEditMode, Placeholder } = commonHooks.useAppContext();
    const [show, setShow] = useState(false);
    const [checked, setChecked] = useState(false);
    const [isInStorage, setIsInStorage] = useState(false);
    const [isAccept, setIsAccept] = useState(false);

    const _isLocalStorageAccessible: boolean = isLocalStorageAccessible();
    const storageName: string = rest?.storageName || '';
    const itemToAddInStorage: any =
        rest?.itemToAddInStorage && JsonQuery(`${rest?.itemToAddInStorage}`, { data }).value;
    const annotations: any = data?.annotations;

    useEffect(() => {
        if (_isLocalStorageAccessible) {
            setTimeout(() => {
                // check whether item is already added to storage or not
                const storageItems = localStorage.getItem(storageName);

                if (storageItems?.includes(itemToAddInStorage)) {
                    setIsInStorage(true);
                } else {
                    setIsInStorage(false);
                    if (
                        annotations?.length > 0 &&
                        annotationTag &&
                        annotations?.find((anno: any) => anno?.tags === annotationTag) &&
                        !inEditMode &&
                        !isInStorage &&
                        !isAccept
                    ) {
                        setShow(true);
                    }
                }
            }, 51); // https://fschopp.github.io/safari-local-storage-bug/?prev-id=1649164027939
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        rest?.itemToAddInStorage,
        _isLocalStorageAccessible,
        annotations?.length,
        annotationTag,
        annotations,
        inEditMode,
    ]);

    const handleClick = () => {
        if (_isLocalStorageAccessible && checked) {
            setTimeout(() => {
                // Add item to local storage
                let items = [itemToAddInStorage];
                const storageItems = localStorage.getItem(storageName);
                storageItems && items?.push(...JSON.parse(storageItems));
                items = _.uniq(items);
                localStorage.setItem(storageName, JSON.stringify(items));
            }, 51); // https://fschopp.github.io/safari-local-storage-bug/?prev-id=1649164027939
        }
        setShow(false);
        setIsAccept(true);
    };

    const renderModalHeader = () => {
        return (
            <>
                <Modal.Title style={{ width: '100%' }} className={'modal-title'}>
                    <HeaderWrapper headings={titles} data={data} />
                </Modal.Title>
                <button
                    className="close-modal-button"
                    onClick={() => {
                        setShow(false);
                        setIsAccept(true);
                    }}
                    aria-label={i18n.translate('widgets_closeModal') as string}
                >
                    <p
                        className="close-button"
                        dangerouslySetInnerHTML={{
                            __html: i18n.format(rest.closeButtonHtml, data),
                        }}
                    />
                </button>
            </>
        );
    };

    const renderModalBody = () => {
        return (
            <>
                <Placeholder
                    contentPlaceHolderId={`top-${CHILDREN_PLACEHOLDER_ID}`}
                    parentWidgetInstanceId={widgetInstance?.id}
                    data={data}
                />
                <div className="button-container">
                    <a
                        className={'decline-link'}
                        href={i18n.format(rest?.linkOnDecline, { ...data })}
                        data-key={i18n.format(rest?.linkOnDecline, { ...data })}
                        id="link"
                        rel="noreferrer"
                        target={rest?.linkTarget}
                        tabIndex={-1}
                    >
                        <button
                            data-testid="decline-button"
                            className="decline-button"
                            aria-label={i18n.format(rest?.declineButtonAriaLabel) as string}
                        >
                            <span
                                className="decline-button-span"
                                dangerouslySetInnerHTML={{
                                    __html: i18n.format(declineButtonText, data),
                                }}
                            />
                        </button>
                    </a>
                    <button
                        className="accept-button"
                        data-testid="accept-button"
                        onClick={() => handleClick()}
                        aria-label={i18n.format(rest?.acceptButtonAriaLabel) as string}
                    >
                        <span
                            className="accept-button-span"
                            dangerouslySetInnerHTML={{
                                __html: i18n.format(acceptButtonText, data),
                            }}
                        />
                    </button>
                </div>
                {rest?.rememberCheckboxLabel && (
                    <div className="checkbox-container">
                        <label>
                            <input
                                id="checkbox-input"
                                className="checkbox-input"
                                data-testId="checkbox-input"
                                type="checkbox"
                                checked={checked}
                                onChange={(e) => setChecked(e.target.checked)}
                            />
                            {i18n.format(rest?.rememberCheckboxLabel, data)}
                        </label>
                    </div>
                )}
                <Placeholder
                    contentPlaceHolderId={`bottom-${CHILDREN_PLACEHOLDER_ID}`}
                    parentWidgetInstanceId={widgetInstance?.id}
                    data={data}
                />
            </>
        );
    };

    return (
        <>
            <Modal
                data-testid="modal-on-load"
                show={show}
                onHide={() => setShow(false)}
                size="lg"
                css={css`
                    ${modalStyles}${theme ? themeStyles : ''}${getTextSchemaProperties(
                        widgetProperties
                    )}${getStyleSchemaProperties(widgetProperties)}${getLayoutSchemaProperties(
                        widgetProperties
                    )}${styles || ''}
                `}
                backdrop="static"
            >
                <div className="modal-custom-header">{renderModalHeader()}</div>
                <div className="modal-body">{renderModalBody()}</div>
            </Modal>
            {inEditMode && showModalInEditMode ? (
                <div
                    css={css`
                        ${modalStyles}${theme ? themeStyles : ''}${getTextSchemaProperties(
                            widgetProperties
                        )}${getStyleSchemaProperties(widgetProperties)}${getLayoutSchemaProperties(
                            widgetProperties
                        )}${styles || ''}
                    `}
                >
                    <div className="modal-custom-header">{renderModalHeader()}</div>
                    <div className="modal-body">{renderModalBody()}</div>
                </div>
            ) : null}
        </>
    );
};

export default ModalOnLoadContainer;
